import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, Eye, EyeOff, KeyRound, Loader2, Lock, LogOut, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { AuroraBackdrop } from '../components/AuroraBackdrop';
import { BrandMark } from '../components/BrandMark';
import { ThemeToggle } from '../components/ThemeToggle';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { useAuth } from '../contexts/AuthContext';
import { initialsOf } from '../utils/format';
import { scorePassword } from '../utils/security';
const toneStyles: Record<string, string> = {
  weak: 'bg-rose-500',
  fair: 'bg-amber-500',
  good: 'bg-sky-500',
  strong: 'bg-emerald-500'
};
export function Security() {
  const navigate = useNavigate();
  const {
    user,
    hasMasterPassword,
    profileLoading,
    profileError,
    createMasterPassword,
    unlockWithMasterPassword,
    logout
  } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const strength = scorePassword(password);
  const creating = !hasMasterPassword;
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (creating) {
      if (password.length < 6) {
        setError('Use at least 6 characters for your master password.');
        return;
      }
      if (password !== confirm) {
        setError('The two passwords do not match. Please re-type them.');
        return;
      }
      setBusy(true);
      try {
        await createMasterPassword(password);
        toast.success('Master password created', {
          description: 'Your dashboard is now unlocked.'
        });
        navigate('/dashboard', {
          replace: true
        });
      } catch (createError) {
        setError(createError instanceof Error ? createError.message : 'Could not save your master password.');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!password) {
      setError('Enter your master password to continue.');
      return;
    }
    setBusy(true);
    try {
      const ok = await unlockWithMasterPassword(password);
      if (!ok) {
        setError('That master password is incorrect.');
        return;
      }
      toast.success('Welcome back');
      navigate('/dashboard', {
        replace: true
      });
    } finally {
      setBusy(false);
    }
  };
  const displayName = user?.displayName || user?.email || 'Signed in';
  return <div className="relative flex min-h-screen w-full flex-col" data-id="element-576" data-mp="29706yp2s8kgr">
      <AuroraBackdrop intensity="marketing" data-id="element-577" data-mp="2543jajydxy5y" />

      <header className="mx-auto flex w-full max-w-5xl items-center justify-between px-5 py-6" data-id="element-578" data-mp="2530woo83ihvu">
        <BrandMark size="sm" data-id="element-579" data-mp="205is1a142otn" />
        <ThemeToggle data-id="element-580" data-mp="fmoprxe8c4cc" />
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center px-5 pb-16" data-id="element-581" data-mp="fln7b7uvwfkl">
        <motion.div initial={{
        opacity: 0,
        y: 18,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.45,
        ease: 'easeOut'
      }} className="glass-strong rounded-3xl p-7 sm:p-9" data-id="element-582" data-mp="kkqccsav6lsk">
          <div className="flex items-center gap-3" data-id="element-583" data-mp="3t1l3xbm7ojrc">
            <div className="relative" data-id="element-584" data-mp="3t2w5r5w3jhft">
              {user?.photoURL ? <img src={user.photoURL} alt={displayName} className="h-12 w-12 rounded-2xl border border-[var(--glass-border)] object-cover" data-id="element-585" data-mp="x0r73xfnjsb" /> : <span className="grid h-12 w-12 place-items-center rounded-2xl brand-gradient text-sm font-semibold text-white" data-id="element-586" data-mp="ap6gg5wavn3t">
                  {initialsOf(displayName)}
                </span>}
              <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white" data-id="element-587" data-mp="3eavmvsm2kafk">
                <ShieldCheck className="h-3 w-3" data-id="element-588" data-mp="1fv4z8u1u90or" />
              </span>
            </div>
            <div className="min-w-0" data-id="element-589" data-mp="1tm9rhb2vmqp2">
              <p className="truncate text-sm font-semibold text-foreground" data-id="element-590" data-mp="1tnbfsh8wl9oq">{displayName}</p>
              <p className="truncate text-xs text-muted-foreground" data-id="element-591" data-mp="1ovb7o1iojah2">{user?.email}</p>
            </div>
          </div>

          <div className="mt-7" data-id="element-592" data-mp="23j61jl8fbkvq">
            <span className="glass-flat inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground" data-id="element-593" data-mp="23mk34r99jq24">
              Step 2 of 2 — Security layer
            </span>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight text-foreground" data-id="element-594" data-mp="28j0udubrekjf">
              {creating ? 'Create your master password' : 'Enter your master password'}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground" data-id="element-595" data-mp="e5svnrknxosi">
              {creating ? 'This second layer protects your student database on this device. Type it twice so we know it matches.' : 'Your Google account is verified. Unlock the dashboard with the master password you created.'}
            </p>
          </div>

          {profileError && <div className="mt-5 flex items-start gap-2.5 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm" data-id="element-596" data-mp="3vga3eg9hx2us">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" data-id="element-597" data-mp="3giq3hnrghxvh" />
              <div className="min-w-0" data-id="element-598" data-mp="3lhucoeedrvlt">
                <p className="text-muted-foreground" data-id="element-599" data-mp="3livm6efr0a1f">{profileError}</p>
                <p className="mt-1 text-xs font-medium text-foreground" data-id="element-600" data-mp="37iukwxvkn0lf">
                  You can continue — setting up your password still works.
                </p>
              </div>
            </div>}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate data-id="element-601" data-mp="15rzqa9d3p7ga">
            <div className="space-y-2" data-id="element-602" data-mp="1aqko624vrf1k">
              <Label htmlFor="master-password" data-id="element-603" data-mp="1apj3r71z5g76">Master password</Label>
              <div className="relative" data-id="element-604" data-mp="18bmi5gyqx3qq">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" data-id="element-605" data-mp="1899t6juklt46" />
                <Input id="master-password" type={reveal ? 'text' : 'password'} value={password} onChange={event => setPassword(event.target.value)} placeholder="At least 6 characters" autoComplete={creating ? 'new-password' : 'current-password'} aria-invalid={Boolean(error) || undefined} className="h-11 rounded-xl pl-9 pr-10" data-id="element-606" data-mp="zag0v5o9vowf" />
                <button type="button" onClick={() => setReveal(value => !value)} aria-label={reveal ? 'Hide password' : 'Show password'} className="absolute right-2.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:text-foreground" data-id="element-607" data-mp="1dah1ck0mkubd">
                  {reveal ? <EyeOff className="h-4 w-4" data-id="element-608" data-mp="2dcnj38hsg13i" /> : <Eye className="h-4 w-4" data-id="element-609" data-mp="2i8ugue0166ad" />}
                </button>
              </div>

              {creating && password.length > 0 && <div className="flex items-center gap-2 pt-1" data-id="element-610" data-mp="2n6w132970bnf">
                  <div className="flex flex-1 gap-1" data-id="element-611" data-mp="2s7tp3vgvvzwu">
                    {[1, 2, 3, 4].map(step => <span key={step} className={`h-1.5 flex-1 rounded-full transition-colors ${step <= strength.score ? toneStyles[strength.tone] : 'bg-[var(--glass-hairline)]'}`} data-id="element-612" data-mp="2w83w9bgewwda" />)}
                  </div>
                  <span className="w-14 text-right text-xs font-medium text-muted-foreground" data-id="element-613" data-mp="3azvxkjjdg9w0">{strength.label}</span>
                </div>}
            </div>

            {creating && <div className="space-y-2" data-id="element-614" data-mp="3jwdb1tf7kjux">
                <Label htmlFor="master-password-confirm" data-id="element-615" data-mp="93in0by22afj">Confirm master password</Label>
                <div className="relative" data-id="element-616" data-mp="538bkjrhurta">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" data-id="element-617" data-mp="52fanzf7uwj2" />
                  <Input id="master-password-confirm" type={reveal ? 'text' : 'password'} value={confirm} onChange={event => setConfirm(event.target.value)} placeholder="Type it again" autoComplete="new-password" aria-invalid={Boolean(error) || undefined} className="h-11 rounded-xl pl-9" data-id="element-618" data-mp="e3dg8m5azc3r" />
                </div>
                {confirm.length > 0 && confirm !== password && <p className="text-xs font-medium text-destructive" data-id="element-619" data-mp="33r4bfcc1sj48">Passwords do not match yet.</p>}
              </div>}

            {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive" data-id="element-620" data-mp="1eaikrfnqcunh">
                {error}
              </p>}

            <Button type="submit" disabled={busy || profileLoading} className="h-12 w-full rounded-2xl brand-gradient text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 hover:opacity-95" data-id="element-621" data-mp="15b59chg1pw04">
              {busy || profileLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-622" data-mp="220w66nbt7v93" /> : <ArrowRight className="mr-2 h-4 w-4" data-id="element-623" data-mp="1o1d68ht5jq7v" />}
              {profileLoading ? 'Checking security profile…' : creating ? 'Create & enter dashboard' : 'Unlock dashboard'}
            </Button>
          </form>

          <button type="button" onClick={async () => {
          await logout();
          navigate('/', {
            replace: true
          });
        }} className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground" data-id="element-624" data-mp="cly99e0y0ycr">
            <LogOut className="h-3.5 w-3.5" data-id="element-625" data-mp="3mvgaxdxcg7kt" />
            Use a different Google account
          </button>
        </motion.div>

        <p className="mt-6 text-center text-xs text-muted-foreground" data-id="element-626" data-mp="392qjuv2ykcgu">
          Only a salted SHA-256 digest of your master password is stored in Firestore — never the password itself.
        </p>
      </main>
    </div>;
}